import { useState, useEffect } from 'react'

import type { Parkinglot, TicketsType } from '@/components/detail/components/shared/parkinglotInfo/model'

import collect from '@/utils/daangnEvents/viewContent'

import type { PoiType } from '@/api/dto/poi'
import { useInitNaverSdk } from '@/shared/hooks/useInitNaverSdk'

import detailApi from '@/api/client/detail'
import { ClientRequestError } from '@/lib/axiosClient'

export interface ParkinglotWithDistance extends Parkinglot {
  distance: number
}

interface useRecommendParkinglotViewModelProps {
  currentParkinglotPosition: { lat: number; lng: number }
  id: string
  geoCodeNeighborsWithSelf: string
}

interface RecommendParkinglotViewModel {
  texts: object
  states: {
    nearPois: PoiType[]
    nearParkinglots: Parkinglot[]
    sortedParkinglots: ParkinglotWithDistance[]
  }
  setters: {
    setNearPois: (pois: PoiType[]) => void
  }
  handlers: {
    getSortedTickets: (tickets: TicketsType[]) => TicketsType[]
    combineNearParkinglots: (poisData: PoiType[]) => PoiType['parkinglots']
    getRecommendParkinglotData: (pois: PoiType[]) => Parkinglot[]
  }
}

const useRecommendParkinglotViewModel = ({
  currentParkinglotPosition,
  id,
  geoCodeNeighborsWithSelf
}: useRecommendParkinglotViewModelProps): RecommendParkinglotViewModel => {
  const { isLoadScript } = useInitNaverSdk()
  const [nearPois, setNearPois] = useState<PoiType[]>([])
  const [nearParkinglots, setNearParkingLots] = useState<Parkinglot[]>([])
  const [sortedParkinglots, setSortedParkinglots] = useState<ParkinglotWithDistance[]>([])

  const getSortedTickets = (tickets: TicketsType[]) => {
    const sortedTickets = tickets.sort((a, b) => {
      if (a.isOpen !== b.isOpen) {
        return (b.isOpen ? 1 : 0) - (a.isOpen ? 1 : 0)
      } else if (a.price !== b.price) {
        return a.price - b.price
      } else {
        return a.couponName.localeCompare(b.couponName)
      }
    })
    const filteredByOpenTickets = sortedTickets.filter((ticket) => ticket.isOpen)
    return filteredByOpenTickets
  }

  useEffect(() => {
    if (window.naver) {
      const newData = nearParkinglots.map((data) => {
        const from = new naver.maps.LatLng(currentParkinglotPosition.lat, currentParkinglotPosition.lng)
        const to = new naver.maps.LatLng(data.latitude, data.longitude)
        const distance = naver.maps.UTMK.getDistance(from, to)
        return { ...data, distance: Math.round(distance) }
      })

      const sortedByDistance = newData.sort((a, b) => a.distance - b.distance)

      const sortedByPrice = sortedByDistance.map((data) => {
        const sortedTickets = getSortedTickets(data.tickets)
        return { ...data, tickets: sortedTickets }
      })

      const filteredByPurchasable = sortedByPrice.filter((data) => data.tickets.length > 0)
      setSortedParkinglots(filteredByPurchasable.slice(0, 3))
    }
  }, [isLoadScript, nearParkinglots])

  const combineNearParkinglots = (poisData: PoiType[]) => {
    const allNearParkinglots = poisData.flatMap((poi) => poi.parkinglots)
    return allNearParkinglots
  }

  const getRecommendParkinglotData = (pois: PoiType[]) => {
    if (!pois) return []
    const allNearParkinglots = combineNearParkinglots(pois)
    const filteredParkinglots = allNearParkinglots.filter(
      (parkinglot) =>
        parkinglot.parkinglotSeq !== Number(id) &&
        parkinglot.isPartner &&
        parkinglot.tickets.length > 0 &&
        !parkinglot.isCarWash &&
        !parkinglot.isAirport
    )
    return filteredParkinglots
  }

  useEffect(() => {
    const getNearParkinglotsByGeoCode = async () => {
      try {
        const { data: resNearPois } = await detailApi.getParkinglotsByGeoCode({ geocode: geoCodeNeighborsWithSelf })
        setNearPois(resNearPois)
      } catch (err: unknown) {
        if (err instanceof ClientRequestError) {
          setNearPois([])
        } else {
          throw err
        }
      }
    }

    collect()
    getNearParkinglotsByGeoCode()
  }, [id])

  useEffect(() => {
    setNearParkingLots(getRecommendParkinglotData(nearPois))
  }, [nearPois])

  return {
    texts: {},
    states: { nearPois, nearParkinglots, sortedParkinglots },
    setters: {
      setNearPois
    },
    handlers: {
      getSortedTickets,
      combineNearParkinglots,
      getRecommendParkinglotData
    }
  }
}

export { useRecommendParkinglotViewModel }
